// Channel 2: the SPA in the iframe talking to the content script that injected it.
//
// `IFrameEventType`'s 75 members are already generated from both mirrors
// (`src/generated/enums.ts`); this is the envelope they travel in and the request/response
// correlation around it, ported from `iframe-event.interface.ts:125` and the
// `source.postMessage` calls in `shortlist-message-handler.service.ts`.
//
// ⚠️ IT LIVES IN `protocol` BECAUSE BOTH ENDS ARE OURS AND NEITHER OWNS IT. The SPA sends and
// the content script answers; putting the envelope in either would make the other import an
// app. It is also why this file is dependency-free vanilla TS — the content script runs
// inside third-party ATS pages (CLAUDE.md), so anything it imports runs there too.
import { IFrameEventType } from './generated/enums.ts'

/** `IWooIFrameEvent<T>` — upstream's four fields, same names, same optionality. */
export interface IFrameMessage<T = unknown> {
  /**
   * Which handler should take it. Upstream registers handlers by this string and asks each
   * `canHandleType(type)`; `shortlist` is the only one that exists.
   *
   * ⚠️ IT IS NOT THE EVENT TYPE. A message with the right `type` and no `target` reaches
   * nobody, which is the shape of "the extension is installed and nothing happens".
   */
  target?: string
  type: string
  data?: T
  /** Correlates a response with its request. Absent on fire-and-forget messages. */
  wooMessageId?: string
}

/** Every message the shortlist frame sends carries this target. */
export const SHORTLIST_TARGET = 'shortlist'

/**
 * The response type for a request type.
 *
 * ⚠️ BY CONVENTION, AND THE CONVENTION IS LOAD-BEARING. Upstream answers
 * `getShortListHandleLocation` with `getShortListHandleLocationResponse` — the same string
 * plus `Response` — and the SPA waits for exactly that. The mapping is derived rather than
 * hand-listed so a new request type cannot be answered with a name nobody is listening for;
 * `iframe-messages.test.ts` checks the derivation against the generated enum, which is what
 * catches the day upstream breaks its own convention.
 *
 * ⚠️ ONE ENUM MEMBER ENDS IN `Response` AND IS NOT ONE.
 * `shortlistContainerStateChangeNotificationResponse` is a BROADCAST to the notifications
 * frame; the request it looks like a reply to — `shortlistContainerStateChange` — is
 * fire-and-forget and is answered with nothing. Never derive it: there is no correlation to
 * carry, and a frame waiting for one would wait forever.
 */
export function responseTypeFor(requestType: string): string {
  return `${requestType}Response`
}

/** The generated enum's members, for tests and for validating an incoming type. */
const KNOWN_TYPES = new Set<string>(Object.values(IFrameEventType))

export function isKnownEventType(type: string): boolean {
  return KNOWN_TYPES.has(type)
}

/**
 * ⚠️ A `postMessage` LISTENER HEARS EVERY MESSAGE ON THE PAGE, including an ATS's own and
 * any other extension's. This is the guard every handler must pass a message through before
 * looking at it: the right shape, our target, and a type the generated enum knows. Upstream
 * does the first two with `canHandleType`; the third is cheap and keeps a typo in one of our
 * own senders from being handled as something else.
 */
export function isIFrameMessage(value: unknown, target = SHORTLIST_TARGET): value is IFrameMessage {
  return hasMessageShape(value) && value.target === target
}

/**
 * The shape half of the guard, without the target check.
 *
 * ⚠️ SEPARATE BECAUSE `undefined` IS A LEGITIMATE TARGET AND A DEFAULT PARAMETER CANNOT SAY SO.
 * `isIFrameMessage(value, request.target)` on a request from the notifications frame — which
 * is given no `?target=` at all (see `resolveTarget`) — passes `undefined`, the default fires,
 * and the frame starts matching responses addressed to `shortlist`. Exact comparison instead,
 * at the two call sites that have a target in hand.
 */
function hasMessageShape(value: unknown): value is IFrameMessage {
  if (typeof value !== 'object' || value === null) return false
  const message = value as Partial<IFrameMessage>
  if (typeof message.type !== 'string' || message.type.length === 0) return false
  return isKnownEventType(message.type)
}

/** A response is correlated by id, and only a response to the request that was sent. */
export function isResponseTo(request: IFrameMessage, value: unknown): value is IFrameMessage {
  if (!hasMessageShape(value)) return false
  if (value.target !== request.target) return false
  if (request.wooMessageId === undefined) return false
  return value.wooMessageId === request.wooMessageId && value.type === responseTypeFor(request.type)
}

let counter = 0

/**
 * A correlation id.
 *
 * ⚠️ NOT `Math.random()` ALONE, AND NOT A BARE COUNTER. Two frames of the same page run this
 * code — the shortlist and the notifications frame, and `all_frames` registration puts the
 * content script in every nested frame too — so a counter collides across them and a random
 * value collides with itself often enough to matter over a long session. `crypto.randomUUID`
 * where it exists, which is everywhere this runs, with a counter-salted fallback.
 */
export function newMessageId(): string {
  counter += 1
  const random = globalThis.crypto?.randomUUID?.()
  return random ?? `woo-${String(Date.now())}-${String(counter)}-${Math.random().toString(36).slice(2, 10)}`
}

export interface RequestOptions<T> {
  type: string
  data?: T
  /**
   * Omit the key for the `shortlist` default; pass it EXPLICITLY as `undefined` for a frame
   * that was given no target. `'target' in options` is what tells the two apart, so a
   * destructuring default would be wrong here — see `hasMessageShape`.
   */
  target?: string
}

function targetOf<T>(options: RequestOptions<T>): { target?: string } {
  const target = 'target' in options ? options.target : SHORTLIST_TARGET
  return target === undefined ? {} : { target }
}

/** A request that expects a `<type>Response` back, carrying a fresh correlation id. */
export function createRequest<T>(options: RequestOptions<T>): IFrameMessage<T> {
  return {
    ...targetOf(options),
    type: options.type,
    wooMessageId: newMessageId(),
    ...(options.data === undefined ? {} : { data: options.data }),
  }
}

/** A fire-and-forget message. No id, because nothing is waiting for one. */
export function createNotification<T>(options: RequestOptions<T>): IFrameMessage<T> {
  return {
    ...targetOf(options),
    type: options.type,
    ...(options.data === undefined ? {} : { data: options.data }),
  }
}

/** The reply to a request: the request's id, the derived type, the payload. */
export function createResponse<T>(request: IFrameMessage, data?: T): IFrameMessage<T> {
  return {
    target: request.target ?? SHORTLIST_TARGET,
    type: responseTypeFor(request.type),
    ...(request.wooMessageId === undefined ? {} : { wooMessageId: request.wooMessageId }),
    ...(data === undefined ? {} : { data }),
  }
}
